import { Search, Filter, SortAsc } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';

export interface FilterOptions {
  search: string;
  fileType: string;
  tagId: string;
  sortBy: string;
}

interface FilterBarProps {
  filters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  availableTags: Array<{ id: string; name: string }>;
}

const FILE_TYPES = [
  { value: '', label: 'All Files' },
  { value: 'images', label: 'Images' },
  { value: 'videos', label: 'Videos' },
  { value: 'documents', label: 'Documents' },
  { value: 'archives', label: 'Archives' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'largest', label: 'Largest First' },
  { value: 'smallest', label: 'Smallest First' },
  { value: 'most-viewed', label: 'Most Viewed' },
];

export function FilterBar({ filters, onFilterChange, availableTags }: FilterBarProps) {
  const activeFiltersCount =
    (filters.fileType ? 1 : 0) +
    (filters.tagId ? 1 : 0) +
    (filters.sortBy !== 'newest' ? 1 : 0);

  const selectedTag = availableTags.find((t) => t.id === filters.tagId);
  const selectedFileType = FILE_TYPES.find((t) => t.value === filters.fileType);
  const selectedSort = SORT_OPTIONS.find((s) => s.value === filters.sortBy);

  const clearFilters = () => {
    onFilterChange({
      search: '',
      fileType: '',
      tagId: '',
      sortBy: 'newest',
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search uploads..."
            value={filters.search}
            onChange={(e) =>
              onFilterChange({ ...filters, search: e.target.value })
            }
            className="pl-10"
          />
        </div>

        {/* File Type Filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="min-w-[140px] justify-between">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                {selectedFileType?.label || 'File Type'}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {FILE_TYPES.map((type) => (
              <DropdownMenuItem
                key={type.value}
                onClick={() =>
                  onFilterChange({ ...filters, fileType: type.value })
                }
              >
                {type.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Tag Filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="min-w-[140px] justify-between">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                {selectedTag?.name || 'Tag'}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[300px] overflow-y-auto">
            <DropdownMenuLabel>Filter by Tag</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onFilterChange({ ...filters, tagId: '' })}
            >
              All Tags
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {availableTags.map((tag) => (
              <DropdownMenuItem
                key={tag.id}
                onClick={() => onFilterChange({ ...filters, tagId: tag.id })}
              >
                {tag.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Sort */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="min-w-[160px] justify-between">
              <div className="flex items-center gap-2">
                <SortAsc className="h-4 w-4" />
                {selectedSort?.label || 'Sort'}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Sort By</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {SORT_OPTIONS.map((sort) => (
              <DropdownMenuItem
                key={sort.value}
                onClick={() =>
                  onFilterChange({ ...filters, sortBy: sort.value })
                }
              >
                {sort.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Active Filters Display */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {filters.fileType && (
            <Badge variant="secondary" className="gap-1">
              Type: {selectedFileType?.label}
            </Badge>
          )}
          {filters.tagId && (
            <Badge variant="secondary" className="gap-1">
              Tag: {selectedTag?.name}
            </Badge>
          )}
          {filters.sortBy !== 'newest' && (
            <Badge variant="secondary" className="gap-1">
              Sort: {selectedSort?.label}
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-6 text-xs"
          >
            Clear All
          </Button>
        </div>
      )}
    </div>
  );
}
